import type { Dispatch, SetStateAction } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '@liminal/web-ui/primitives/Button';
import { IconButton } from '@liminal/web-ui/primitives/IconButton';
import { Input } from '@liminal/web-ui/forms/Input';
import { resolveDeviceTimezone } from '@liminal/utils/date';
import { newRowKey, type CatalogueErrors, type CatalogueForm, type WindowRow } from '../catalogueForm';

interface ActiveWindowsEditorProps {
  readonly form: CatalogueForm;
  readonly setForm: Dispatch<SetStateAction<CatalogueForm>>;
  readonly errors: CatalogueErrors['windows'];
}

/**
 * The spans of time in which a Pix payment at the partner qualifies for a
 * reward; the partner shows in the app only inside one, and a payment made
 * in one can be redeemed after it closes. Each is picked as wall-clock
 * times in the operator's own timezone and sent as the UTC moments they name
 * (LIM-1402): the browser's Date does the conversion, offset and DST
 * included, never arithmetic here.
 */
export function ActiveWindowsEditor({ form, setForm, errors }: ActiveWindowsEditorProps) {
  // Named so an operator in one zone setting a launch for another can tell.
  const zone = resolveDeviceTimezone();

  const setWindow = (key: string, patch: Partial<WindowRow>) =>
    setForm((f) => ({ ...f, windows: f.windows.map((row) => (row.key === key ? { ...row, ...patch } : row)) }));

  const addWindow = () => {
    const key = newRowKey();
    setForm((f) => ({ ...f, windows: [...f.windows, { key, start: '', end: '' }] }));
  };

  return (
    <Stack spacing={2}>
      {form.windows.map((row) => (
        <Stack
          key={row.key}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="flex-start"
          data-testid="window-row"
        >
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <Input
              label="Opens"
              type="datetime-local"
              value={row.start}
              onChange={(start) => setWindow(row.key, { start })}
              error={errors[row.key]?.start}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <Input
              label="Closes"
              type="datetime-local"
              value={row.end}
              onChange={(end) => setWindow(row.key, { end })}
              min={row.start || undefined}
              error={errors[row.key]?.end}
            />
          </Box>
          {/* Level with the inputs, below their labels. */}
          <Box sx={{ height: 56, mt: { sm: 3 }, display: 'flex', alignItems: 'center' }}>
            <IconButton
              icon="close"
              label="Remove window"
              size="sm"
              tone="muted"
              onClick={() => setForm((f) => ({ ...f, windows: f.windows.filter((w) => w.key !== row.key) }))}
            />
          </Box>
        </Stack>
      ))}
      <Stack direction="row">
        <Button variant="link" size="sm" startIcon="plus" onClick={addWindow}>
          Add window
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {`No windows means always on. Payments made during a window can be redeemed after it closes. Times are in ${zone ? `your timezone, ${zone}` : "this browser's timezone"}.`}
      </Typography>
    </Stack>
  );
}
